<template>
	<section>
		<!-- Meta tags -->
		<Title>{{ pageTitle }}</Title>
		<Meta name="description" :content="pageDescription" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta property="og:title" :content="pageTitle" />
		<Meta property="og:description" :content="pageDescription" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<HeroSubpage
			type="jobs"
			:title="heroTitle"
			:subtitle="$t('jobhero.subtitle')"
			:searchData="{ paramValues: '', listLength: totalResults }"
		/>

		<div class="flex flex-col items-center w-full">
			<SearchbarJobs navigate-to-location-path @run-search="() => {}" @fields-data="() => {}" />
		</div>

		<div id="job-results">
			<JobsPageList
				:title="heroTitle"
				show-results-number
				:url="jobsUrl"
				:address-resolver="resolveCityAddress"
				@set-page="setPage"
				@get-results-number="totalResults = $event"
			/>
		</div>
	</section>
</template>

<script setup lang="ts">
import type { locationObject } from "~/types";
import { addArrayToParams } from "~/composables/utils";
import { constructApiUrl } from "~/composables/helpers";
import { useLocationPageSchema } from "~/composables/microdata/useLocationPageSchema";
import { createCityAddressResolver, normalizeLocationSlug } from "~/composables/jobCardAddress";

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

const citySlug = String(route.params.citySlug);
const normalizedCitySlug = normalizeLocationSlug(citySlug);
const regionCode = useRegion.getCurrentRegion().code.toLowerCase();
const totalResults = ref(0);

// Redirect if slug needs normalization (e.g., "budapest-1" → "budapest").
// navigateTo() will abort further execution and return a redirect response.
if (citySlug !== normalizedCitySlug) {
	await navigateTo(
		{
			path: `/${locale.value}/jobs/${normalizedCitySlug}`,
			query: route.query,
		},
		{ redirectCode: 301, replace: true }
	);
}

// Resolve the location tag by type + slug. The API returns a structured
// JSON 404 with a descriptive message when the slug doesn't exist.
const { data: tagData, error: tagError } = await useFetch<{ data: locationObject }>(
	`${globalConfig.API}/tags/office-location/${normalizedCitySlug}`,
	{ server: true, key: `tag-office-location-${normalizedCitySlug}` }
);

if (tagError.value || !tagData.value?.data) {
	throw createError({ statusCode: 404, statusMessage: "Location not found" });
}

const locationTag = tagData.value.data;

const cityName = computed(() => {
	const name = locationTag.name as Record<string, string>;
	return name[locale.value] || name["en"] || name["ro"] || name["hu"] || normalizedCitySlug;
});

const heroTitle = computed(() => `${cityName.value} ${t("locationJobs.heroTitleSuffix")}`);
const pageTitle = computed(() => `${cityName.value} ${t("locationJobs.seoTitleSuffix")}`);
const pageDescription = computed(() => t("locationJobs.seoDescription", { city: cityName.value }));

const resolveCityAddress = createCityAddressResolver({
	citySlug: normalizedCitySlug,
	cityName: () => cityName.value,
	locale: () => locale.value,
});

// Build the jobs API URL filtered by this city location tag.
const jobsUrl = computed(() => {
	const url = constructApiUrl("/jobs");
	url.searchParams.append("region", regionCode);
	addArrayToParams("tags[office-location][]", [String(locationTag.id)], url);

	const queryParams = route.query;
	const keywordParam = queryParams.keyword || queryParams.query;
	if (keywordParam) url.searchParams.append("keyword", keywordParam.toString());
	if (queryParams.category) addArrayToParams("tags[job-category][]", queryParams.category.toString().split(","), url);
	if (queryParams.subcategory)
		addArrayToParams("tags[job-subcategory][]", queryParams.subcategory.toString().split(","), url);
	if (queryParams.type) addArrayToParams("tags[job-type][]", queryParams.type.toString().split(","), url);
	if (queryParams.extra) addArrayToParams("badges[]", queryParams.extra.toString().split(","), url);
	if (queryParams.level) addArrayToParams("tags[job-level][]", queryParams.level.toString().split(","), url);
	if (queryParams.salary) url.searchParams.append("scope[]", "hasVisibleSalary");
	if (queryParams.new_job) url.searchParams.append("scope[]", "latestJobs");
	if (queryParams.expiring) url.searchParams.append("scope[]", "expiring");

	if (route.query.page) url.searchParams.append("page", String(route.query.page));
	if (globalConfig.isBlue) url.searchParams.append("scope[]", "isBlue");
	if (globalConfig.isNotBlueHU) url.searchParams.append("scope[]", "isNotBlue");
	return url.href;
});

function setPage(page: number) {
	const query = { ...route.query };
	if (page > 1) {
		query.page = String(page);
	} else {
		delete query.page;
	}

	router.push({
		path: `/${locale.value}/jobs/${normalizedCitySlug}`,
		query,
	});
}

const locationTagRef = ref(locationTag);
const canonicalUrl = computed(() => `${globalConfig.host}${route.fullPath}`);
useLocationPageSchema(locationTagRef, pageTitle, pageDescription, canonicalUrl, locale);
</script>
